import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiLogOut, FiChevronDown } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import PaperboyWordmark from './PaperboyWordmark';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && dropdownOpen) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dropdownOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const navItems = [
    { path: '/feed', label: 'Feed' },
    { path: '/search', label: 'Search' },
    ...(user?.is_superuser ? [{ path: '/admin', label: 'Admin' }] : []),
  ];

  return (
    <header className="pb-header">
      <div className="pb-header-inner">
        <Link
          to={isAuthenticated ? '/feed' : '/'}
          className="pb-header-brand"
          aria-label="PaperBoy"
        >
          <PaperboyWordmark size={20} />
        </Link>

        {isAuthenticated ? (
          <>
            <nav className="pb-header-nav" aria-label="Primary">
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`pb-header-nav-link${active ? ' active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="pb-header-actions">
              <form onSubmit={handleSearch} className="pb-header-search">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pb-header-search-input"
                  aria-label="Search articles"
                />
                <button
                  type="submit"
                  className="pb-header-search-btn"
                  aria-label="Submit search"
                >
                  <FiSearch size={14} />
                </button>
              </form>

              <div className="pb-header-user" ref={dropdownRef}>
                <button
                  type="button"
                  className="pb-header-user-trigger"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                  aria-label="Account menu"
                >
                  <span className="pb-header-avatar">
                    {getUserInitials()}
                    <span className="pb-header-avatar-dot" aria-hidden="true" />
                  </span>
                  <FiChevronDown
                    size={14}
                    className={`pb-header-arrow ${dropdownOpen ? 'open' : ''}`}
                  />
                </button>

                {dropdownOpen && (
                  <div className="pb-header-dropdown" role="menu">
                    <div className="pb-header-dropdown-head">
                      <span className="pb-header-dropdown-name">{user?.name || 'User'}</span>
                      <span className="pb-header-dropdown-email">{user?.email}</span>
                    </div>
                    <div className="pb-header-dropdown-divider" />
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="pb-header-dropdown-item"
                      role="menuitem"
                    >
                      <FiLogOut size={14} />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="pb-header-actions pb-header-auth">
            <Link to="/login" className="pb-btn pb-btn-sm pb-btn-ghost">
              Sign In
            </Link>
            <Link to="/register" className="pb-btn pb-btn-sm pb-btn-primary">
              Get Started
              <span className="pb-btn-dot" style={{ background: '#2563eb' }} />
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
